import * as React from 'react';
import {CompositeItem} from '@floating-ui/react';

import {mergeProps, mergeRefs} from '../../utils';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';

import Button, {ButtonProps} from '../Button';
import {useSegmentedControlContext} from './SegmentedControl';
import IconButton, {IconButtonProps} from '../IconButton';
import Tooltip from '../Tooltip';

type SpringButtonProps =
  | Pick<
      ButtonProps,
      'children' | 'prefix' | 'suffix' | 'justify' | 'truncationProps'
    >
  | (Pick<IconButtonProps, 'Icon' | 'label'> & {children: never});

type SegmentedControlButtonProps = {
  isDisabled?: boolean;
  isSelected?: boolean;
  value: string;
} & SpringButtonProps &
  Partial<
    Pick<IconButtonProps, 'Icon' | 'label' | 'tooltipProps' | 'withTooltip'>
  > &
  Omit<HTMLAttributes, 'prefix'> &
  SpringTooling;

type SegmentedControlRadioProps = SegmentedControlButtonProps & {
  handleToggle: () => void;
  hasValue?: boolean;
  keyboardActivation: 'auto' | 'manual';
};

const SegmentedControlRadio = React.forwardRef<
  HTMLButtonElement,
  SegmentedControlRadioProps
>(({hasValue, ...props}: SegmentedControlRadioProps, forwardRef) => {
  const {keyboardActivation, handleToggle} = props;

  const handleClick =
    keyboardActivation === 'manual' ? handleToggle : undefined;
  const handleFocus = keyboardActivation === 'auto' ? handleToggle : undefined;

  return (
    <CompositeItem
      onFocus={handleFocus}
      render={(htmlProps) => {
        // @ts-expect-error - ref is not included in FloatingUI's type definition despite being passed through
        const {onFocus, ref, tabIndex: tabIndexFromFloatingUI} = htmlProps;
        const mergedRefs = mergeRefs([ref, forwardRef]);
        // When a value is set we override floating-ui's tabIndex from htmlProps using our own `isChecked` logic
        // because we don't have access to the index to set activeIndex. Note: There is no uncontrolled support.
        const tabIndex = hasValue
          ? props['aria-checked']
            ? 0
            : -1
          : tabIndexFromFloatingUI;

        const mergedProps = mergeProps(
          {
            onClick: handleClick,
            onFocus,
            ref: mergedRefs,
            role: 'radio',
            tabIndex,
          },
          props
        );

        /**
         * Using double-equals, as it will catch both `null` and `undefined`.
         */
        if (props.children != null) {
          if (props.withTooltip) {
            return (
              <Tooltip
                asChild
                label={props.label ?? props.children}
                {...props.tooltipProps}
              >
                <Button {...mergedProps}>{props.children}</Button>
              </Tooltip>
            );
          }

          return <Button {...mergedProps}>{props.children}</Button>;
        }

        return <IconButton {...(mergedProps as IconButtonProps)} />;
      }}
    />
  );
});

const SegmentedControlCheckbox = React.forwardRef<
  HTMLButtonElement,
  SegmentedControlButtonProps
>(({...props}, ref) => {
  if (props.children !== null || props.children !== undefined) {
    if (props.withTooltip) {
      return (
        <Tooltip
          asChild
          label={props.label ?? props.children}
          {...props.tooltipProps}
        >
          <Button ref={ref} role="checkbox" {...(props as ButtonProps)}>
            {props.children}
          </Button>
        </Tooltip>
      );
    }

    return <Button ref={ref} role="checkbox" {...(props as ButtonProps)} />;
  }

  return (
    <IconButton ref={ref} role="checkbox" {...(props as IconButtonProps)} />
  );
});

export const SegmentedControlButton = React.forwardRef<
  HTMLButtonElement,
  SegmentedControlButtonProps
>(
  (
    {
      isDisabled: isDisabledProp,
      isSelected,
      value,
      ...props
    }: SegmentedControlButtonProps,
    ref
  ) => {
    const {
      isDisabled: isDisabledContext,
      getIsSelected,
      hasValue,
      keyboardActivation,
      selectionMode,
      toggleSelectedValue,
    } = useSegmentedControlContext();
    const isChecked = isSelected ?? getIsSelected(value);

    const mergedProps = mergeProps(
      {
        'aria-checked': isChecked,
        isDisabled: isDisabledProp || isDisabledContext,
        ref,
      },
      props
    );

    const handleToggle = React.useCallback(() => {
      if (!mergedProps.isDisabled) {
        toggleSelectedValue(value);
      }
    }, [mergedProps.isDisabled, toggleSelectedValue, value]);

    if (selectionMode === 'single') {
      return (
        <SegmentedControlRadio
          handleToggle={handleToggle}
          hasValue={hasValue}
          keyboardActivation={keyboardActivation}
          {...(mergedProps as SegmentedControlButtonProps)}
        />
      );
    }

    return (
      <SegmentedControlCheckbox
        onClick={handleToggle}
        {...(mergedProps as SegmentedControlButtonProps)}
      />
    );
  }
);
